// 模組07 訊息功能 — mock 通知排程器(9月交付 ticket 11)。
//
// 「設定要真的驅動通知,不只是把參數存起來」(取代全站畫面票 14 的要求):
// 每次讀取通知 feed 前,把「生效中的規則 × 示範情境」餵進升級判定純函式
// (helpers/notifyEscalation,與單元測試同一顆),把結果實體化進 notification store:
//   - 每條情境只留「最新一則」提醒(第幾階由判定函式說了算,前綴跟著階數)
//   - 停止條件成立 → 補結案訊息(closeMsgKey;到期/審核類不另發,判定函式回 null)
//   - 規則暫停/刪除 → 該情境的產生列整批回收(未來不再發)
// 實體化而不是虛擬列:feed 的已讀/暫停操作走既有 PATCH,產生列要有真實 id 才動得了。
// 已存在的列(genKey 相同)不重建 —— 已讀狀態不會被排程重置。
import { evaluateNotifyRule } from "../helpers/notifyEscalation";
import { STAGE_PREFIX } from "../constants/notifyRules";
import { getResourceStore } from "./crudFactory";
import { notifyScheduleScenarios } from "./db/notifyScheduleScenarios";

const fmtDate = (ms) => {
  const d = new Date(ms);
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
};

/**
 * 二三階訊息照確認單版式包裹(第 7/8 行,一字不差):
 *   二階:【第二階｜單位主管】以下事項自{首次通知時間}起已通知{已通知次數}次仍未處理:<第一階原文案>
 *   三階:【第三階｜跨單位主管】以下事項已逾{逾時時長}未處理,請協助介入:<第一階原文案>
 */
function escalatedMessage(latest, occurrences, body, now) {
  if (latest.stage <= 1) return body;
  const first = occurrences[0];
  if (latest.stage === 2) {
    return `${STAGE_PREFIX[2]}以下事項自 ${fmtDate(first.at)} 起已通知 ${occurrences.length} 次仍未處理：${body}`;
  }
  const overdueDays = Math.max(1, Math.round((now - first.at) / 86400000));
  return `${STAGE_PREFIX[3]}以下事項已逾 ${overdueDays} 天未處理，請協助介入：${body}`;
}

export function syncNotifyFeed(now = Date.now()) {
  const rules = getResourceStore("notify-rule");
  const feed = getResourceStore("notification");
  if (!rules || !feed) return;

  notifyScheduleScenarios.forEach((sc) => {
    // 同一條規則可以盯多件事(key 區分),回收/重建只動自己這件
    const genPrefix = `rule-${sc.ruleId}:${sc.key}:`;
    const existing = () => feed.all().filter((n) => (n.genKey || "").startsWith(genPrefix));
    const rule = rules.find(sc.ruleId);

    // 規則不存在(刪除核准)或暫停中:不產生,已產生的一併回收
    if (!rule || rule.active === false) {
      existing().forEach((n) => feed.remove(n.id));
      return;
    }

    const ctx = sc.context(now);
    const { occurrences, closed, closeMsgKey } = evaluateNotifyRule(rule, ctx, now);

    const wanted = [];
    const latest = occurrences[occurrences.length - 1];
    if (latest) {
      // 鍵用「第幾則+第幾階」而不是時間戳:情境時間軸錨在「相對現在」,
      // 時間戳每次同步都會漂移幾毫秒 → 列被拆掉重建,已讀狀態跟著蒸發。
      // 次數/階數只在真的有新一則(或升階)時改變 —— 那時換列才是對的。
      wanted.push({
        genKey: `${genPrefix}occ-${occurrences.length}-s${latest.stage}`,
        stage: latest.stage,
        message: escalatedMessage(latest, occurrences, sc.body, now),
        msgKey: sc.msgKey,
        createdAt: new Date(latest.at).toISOString(),
        overdue: latest.stage >= 3,
      });
    }
    if (closed && closeMsgKey && sc.closeBody) {
      wanted.push({
        genKey: `${genPrefix}close`,
        stage: 1,
        message: sc.closeBody,
        msgKey: closeMsgKey,
        createdAt: new Date(ctx.resolvedAt).toISOString(),
        overdue: false,
      });
    }

    // 發送管道由該階的規則設定推導 ——「設定要真的驅動通知」含管道維度
    const channelOf = (stage) => {
      const st = (rule.stages || []).find((x) => x.level === stage);
      const mail = st ? st.channels?.mail !== false : true;
      const inApp = st ? st.channels?.inApp !== false : true;
      return inApp && mail ? "站內＆Email" : inApp ? "站內" : "Email";
    };

    // 回收過時的產生列(升階後,舊階那則以最新一則取代)
    existing().forEach((n) => {
      if (!wanted.some((w) => w.genKey === n.genKey)) feed.remove(n.id);
    });
    // 補上缺的;已存在的不動(保留已讀狀態)
    const present = existing();
    wanted.forEach((w) => {
      if (present.some((n) => n.genKey === w.genKey)) return;
      feed.add({
        category: rule.category,
        source: sc.source,
        type: sc.type,
        docNo: sc.docNo || null,
        channel: channelOf(w.stage || 1),
        read: false,
        actionLabel: sc.actionLabel,
        ...w,
      });
    });
  });
}
